<script>
import GmapMarker from 'gmap-vue/dist/esm/components/marker-icon.vue'
import GmapMap from 'gmap-vue/dist/esm/components/map-layer.vue'
import mapOptions from '@/modules/mixins/mapOptions'
import Modal from '@/components/atoms/Modal'
import InputTextarea from '@/components/atoms/inputlib/components/InputTextarea'
import InputLine from '@/components/atoms/inputlib/components/InputLine'
import InputPhone from '@/components/atoms/inputlib/components/InputPhone'
import FormValidation from '@/components/atoms/FormValidation'
import RadioAddress from '@/components/molecules/input/RadioAddress'
import ButtonLoader from '@/components/atoms/ButtonLoader'
import { mapGetters } from 'vuex'
import { Address } from '@/api'
import { periodsUsableForCart } from '@/modules/helpers/deliveryPeriodSlots'

export default {
    name: 'DeliveryDetailsBlock',
    components: {
        ButtonLoader,
        FormValidation,
        InputPhone,
        InputLine,
        InputTextarea,
        GmapMarker,
        GmapMap,
        Modal,
        RadioAddress,
    },

    mixins: [mapOptions],

    props: {
        loading: Boolean,
        unavailableAddresses: {
            type: Array,
            default: () => [],
        },
    },

    data() {
        return {
            loadingDeliveryPeriods: false,
            loadingSave: false,
            selectedAddress: null,
            validPhone: false,
            phone: null,
            comment: null,
            subAddress: null,
            address: null,
            addresses: null,
            // tasks#170: the automatic switch away from an unavailable ASAP is running / was done for this key
            autoScheduling: false,
            autoScheduledKey: null,
            // tasks#170: the period fetch was retried for this key (once per unknown episode)
            periodRetriedKey: null,
        }
    },

    computed: {
        ...mapGetters({
            restaurant: 'checkout/restaurant',
            asap: 'order/asap',
            orderAddress: 'order/address',
            locale: 'locale',
            activeCanteen: 'active/canteen',
            deliveryPeriod: 'deliveryPeriod',
        }),
        hasActiveCanteen() {
            return !!this.activeCanteen?.id
        },
        deliveryTime() {
            return this.hasActiveCanteen
                ? this.activeCanteen.delivery_time
                : this.asap
                ? this.restaurant.next_delivery
                : this.$cart.calculation.required_delivery_time
        },
        isSechuled() {
            return !this.asap || this.hasActiveCanteen
        },
        scheduledTime() {
            if (!this.isSechuled) return
            return this.$moment(this.deliveryTime).calendar(null, {
                sameDay: () => {
                    return `[${this.$t('base.today')}] ${this.$hour} - ${this.$moment(
                        this.deliveryTime
                    )
                        .add(this.$deliveryPeriod, 'minutes')
                        .format(this.$hour)}`
                },
                nextDay: this.deliveryTomorrow,
                nextWeek: this.deliveryOn,
                lastDay: this.deliveryOn,
                lastWeek: this.deliveryOn,
                sameElse: this.deliveryOn,
            })
        },
        asapTime() {
            if (
                !this.deliveryAddressDetails.max_delivery_time &&
                !this.deliveryAddressDetails.min_delivery_time
            )
                return
            const max = this.deliveryAddressDetails.max_delivery_time
            const min = this.deliveryAddressDetails.min_delivery_time

            return `${max === min ? max : `${min}-${max}`} ${this.$t('base.minute')}`
        },
        deliveryTomorrow() {
            return `[${this.$t('base.tomorrow')}] ${this.$hour} - ${this.$moment(this.deliveryTime)
                .add(this.$deliveryPeriod, 'minutes')
                .format(this.$hour)}`
        },
        deliveryOn() {
            return `[${this.$t('order.delivery_on')}] ${
                this.locale === 'hu' ? this.$dayMonthTimeFormat_HU : this.$dayMonthTimeFormat_EN
            } - ${this.$moment(this.deliveryTime)
                .add(this.$deliveryPeriod, 'minutes')
                .format(this.$hour)}`
        },
        isCompanyAddress() {
            return this.address?.is_company_address
        },
        addressBlockMapOptoins() {
            return {
                ...this.mapOptions,
                ...{
                    minZoom: 16,
                    maxZoom: 16,
                    zoom: 16,
                    gestureHandling: 'none',
                    keyboardShortcuts: false,
                },
            }
        },
        cart() {
            return this.$cart.model
        },
        deliveryAddressDetails() {
            return this.$cart.calculation.delivery_address_details
        },
        asapEnabled() {
            return this.deliveryPeriod?.immediate_available
        },
        cartRestaurantId() {
            return this.getId(this.$cart.calculation?.restaurant)
        },
        // tasks#170: can ASAP be judged for this basket? 'available' | 'unavailable' | 'unknown' — unknown without usable
        // periods of the cart's restaurant (not loaded yet, failed fetch, periods of another restaurant); the same
        // notion as cartWatcher's cartPeriodsUsable() / selectedDeliveryTimeUnavailable()
        asapAvailability() {
            if (!periodsUsableForCart(this.$store.state.deliveryPeriod, this.cartRestaurantId))
                return 'unknown'
            return this.asapEnabled && !this.deliveryAddressDetails?.restaurant_area_is_pre_order
                ? 'available'
                : 'unavailable'
        },
        // tasks#170: ASAP is rendered (and handled) as an inactive option. A selected ASAP is never rendered inactive
        // while its availability is unknown; an unselected one keeps the last periods' answer (pre-existing)
        asapInactive() {
            if (this.asapAvailability === 'unknown') return !this.asap && !this.asapEnabled
            return this.asapAvailability === 'unavailable'
        },
        // tasks#170: an inactive option is never shown selected; a truly unavailable ASAP is switched to a scheduled
        // window right away (autoScheduleKey), until then (or when no window is offered) neither option is checked
        asapChecked() {
            return this.asap && !this.asapInactive
        },
        // tasks#170: a period request is on its way (e.g. cartWatcher's refetch after an area / pre-order change), or the
        // committed periods offer ASAP while the calculation says pre-order only — core-api folds the pre-order rule into
        // immediate_available, so these periods were computed for another basket and are about to be replaced
        periodsSettling() {
            return (
                this.$store.getters.deliveryPeriodLoading ||
                (this.asapAvailability !== 'unknown' &&
                    !!this.asapEnabled &&
                    !!this.deliveryAddressDetails?.restaurant_area_is_pre_order)
            )
        },
        // the first window that still starts in the future (the one "schedule for later" preselects)
        firstOfferedFrom() {
            const now = this.$moment()
            for (const ranges of Object.values(this.deliveryPeriod?.days || {})) {
                const range = (ranges || []).find((r) => now.isBefore(r?.from))
                if (range) return range.from
            }
            return undefined
        },
        // tasks#170: set while a selected ASAP is known unavailable for this basket and a window can replace it; the
        // switch waits for every running load, so a calculate in flight (which moves a closed-now ASAP of an eod order
        // itself, Cart.onMovedDeliveryTime) decides first — after a "moved" answer ASAP is no longer selected
        autoScheduleKey() {
            if (!this.asap || this.hasActiveCanteen || this.asapAvailability !== 'unavailable')
                return null
            if (this.loading || this.loadingDeliveryPeriods || this.$cart.loading) return null
            if (this.periodsSettling) return null
            if (!this.firstOfferedFrom) return null
            return `${this.cartRestaurantId}|${this.firstOfferedFrom}`
        },
        // tasks#170: set while a selected ASAP cannot be judged (failed fetch, unusable answer, periods of another
        // restaurant): the periods of the cart's restaurant are fetched once more per reason
        periodRetryKey() {
            if (!this.asap || this.hasActiveCanteen) return null
            if (this.loading || this.loadingDeliveryPeriods || this.$cart.loading) return null
            if (this.$store.getters.deliveryPeriodLoading || !this.cartRestaurantId) return null
            // periods that contradict the basket (see periodsSettling) and nobody refetches them
            if (this.periodsSettling) return `${this.cartRestaurantId}|stale`
            if (this.asapAvailability !== 'unknown') return null
            const periodState = this.$store.state.deliveryPeriod
            // the first answer of the page is still on its way (the checkout fetches it with the restaurant)
            if (periodState.committedRestaurantId == null && !periodState.lastFetchFailed)
                return null
            return `${this.cartRestaurantId}|${
                periodState.lastFetchFailed ? 'failed' : periodState.committedRestaurantId
            }`
        },
        addressChange: {
            get() {
                return this.$store.getters['checkout/addressChange']
            },
            set(value) {
                this.$store.dispatch('checkout/setAddressChange', value)
            },
        },
    },

    watch: {
        autoScheduleKey(key) {
            this.scheduleAwayFromUnavailableAsap(key)
        },
        periodRetryKey(key) {
            this.retryPeriodFetch(key)
        },
        asapAvailability(availability) {
            // usable periods again: a later unknown episode may retry once more
            if (availability !== 'unknown') this.periodRetriedKey = null
            // ASAP is available again: a later unavailability may switch again
            if (availability === 'available') this.autoScheduledKey = null
        },
        '$cart.address': {
            immediate: true,
            handler(cartAddress) {
                if (!this.address && cartAddress && this.getId(cartAddress) !== this.address?.id) {
                    this.initAddress()
                }
            },
        },
    },

    mounted() {
        // tasks#170 (replaces the #894 workaround): judge the state the block starts in, later changes reach the watchers
        this.scheduleAwayFromUnavailableAsap(this.autoScheduleKey)
        this.retryPeriodFetch(this.periodRetryKey)
    },

    methods: {
        changeAddress() {
            this.initAddress(true)
        },
        resetChangeAddress() {
            this.selectedAddress = null
            this.addressChange = false
        },
        async initAddress(changeAddress = undefined) {
            await this.$addresses.initAddresses()
            this.address = this.$cart?.address
                ? this.$addresses.findItem(this.getId(this.$cart.address))?.model
                : undefined
            this.selectedAddress = this.address?.id ? this.address : null
            if (this.hasActiveCanteen) this.addressChange = false
            else
                this.addressChange =
                    typeof changeAddress === 'boolean' ? changeAddress : !this.address?.id
        },
        setAsap() {
            if (this.loadingDeliveryPeriods || this.loading || this.asap || this.asapInactive)
                return
            this.$time.asap()
        },
        // tasks#170: a selected ASAP turned out unavailable (slow / later period fetch, restaurant closed meanwhile,
        // basket flipped to pre-order only, address / zone change): select the first offered window, the same one
        // "schedule for later" preselects, and show it in the time modal unless the "delivery time changed" alert
        // already tells the customer. Once per key: if the server turns the order back into ASAP for the same
        // periods, the block shows no option checked instead of switching back and forth
        async scheduleAwayFromUnavailableAsap(key) {
            if (!key || this.autoScheduling || key === this.autoScheduledKey) return
            if (!this.$time?.scheduleFirstOffered) return
            this.autoScheduling = true
            // a watcher of the same change (cartWatcher's period refetch) may start a request later in this flush:
            // decide on the state after it
            await this.$nextTick()
            if (this.autoScheduleKey !== key) {
                this.autoScheduling = false
                return
            }
            this.autoScheduledKey = key
            try {
                const switched = await this.$time.scheduleFirstOffered()
                if (switched && !this.$store.getters['active/deliveryTimeChanged'])
                    this.$time.open()
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error(`Error during scheduling away from ASAP: ${e}`)
            } finally {
                this.autoScheduling = false
            }
        },
        // tasks#170: ASAP stays selected (rendered as usual) while its availability is unknown; fetch the cart
        // restaurant's periods once more, the watchers decide again on the answer
        async retryPeriodFetch(key) {
            if (!key || key === this.periodRetriedKey) return
            // another watcher of the same change may start the refetch later in this flush
            await this.$nextTick()
            if (this.periodRetryKey !== key || key === this.periodRetriedKey) return
            this.periodRetriedKey = key
            try {
                this.loadingDeliveryPeriods = true
                await this.$store.dispatch('getDeliveryPeriod', {
                    restaurantId: this.cartRestaurantId,
                    allowanceId: this.$store.getters['allowance']?.id,
                    force: true,
                })
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error(`Error during loading delivery periods: ${e}`)
            } finally {
                this.loadingDeliveryPeriods = false
            }
        },
        async changeTime() {
            if (this.loadingDeliveryPeriods || this.loading) return
            try {
                this.loadingDeliveryPeriods = true
                await this.$store.dispatch('getDeliveryPeriod', {
                    restaurantId: this.restaurant.id,
                    allowanceId: this.$store.getters['allowance']?.id,
                    force: true,
                })
                if (!this.$cart.loading) this.$time.schedule()
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error(`Error during loading delivery periods: ${e}`)
            } finally {
                this.loadingDeliveryPeriods = false
            }
        },
        edit() {
            if (!this.loading) {
                this.phone = this.$cart.model.phone
                this.comment = this.$cart.model.comment
                this.subAddress = this.$cart.model.delivery_sub_address
                this.$refs.modalEditDeliveryInfo.openModal()
            }
        },
        async savePhoneNumber() {
            try {
                this.loadingSave = true
                if (this.isCompanyAddress) {
                    await this.$user.update({
                        ...this.$user.model,
                        ...{ phone: this.phone },
                    })
                } else {
                    const address = new Address({
                        ...this.orderAddress,
                        ...{ phone: this.phone },
                    })
                    await address.save()
                    await this.$addresses.fetch()
                }
            } finally {
                this.loadingSave = false
            }
        },
        cancelEdit() {
            this.phone = null
            this.comment = null
            this.subAddress = null
            this.$refs.modalEditDeliveryInfo.closeModal()
        },
        submit() {
            this.$cart.model.phone = this.phone
            this.$cart.model.comment = this.comment
            this.$cart.model.delivery_sub_address = this.subAddress
            this.$cart.calculate()
            this.cancelEdit()
        },
        confirmEdit() {
            this.$refs.form.formSubmit()
        },
        addNewAddress() {
            this.$location.add.open(
                this.orderAddress.id ? undefined : this.orderAddress,
                () => {
                    this.selectedAddress = this.orderAddress
                },
                true
            )
        },
        selectAddress(address) {
            this.selectedAddress = address
        },
        setAddress() {
            this.$location.select.set(this.selectedAddress.model, false, this.initAddress)
            this.resetChangeAddress()
        },
        validatePhone(isValid) {
            this.validPhone = isValid
        },
    },
}
</script>

<template lang="pug">
    
.card
    .card-body(:key="`deliveryDetailsBlock-${address ? address.id : '0'}`")
        h2.mb-6 {{ $t('details.delivery_details') }}
        
        .d-flex.justify-content-between.align-items-center.gap-4.mb-4.h-xs
            h3.mb-0 {{ $t('details.delivery_time') }}
            transition(name="fade" mode="out-in")
                span(v-if="hasActiveCanteen")  {{ scheduledTime }}
                span(v-else-if="asapChecked && asapTime"  key="main-1") {{ asapTime }}
                span(v-else-if="!asap && scheduledTime && !$cart.loading" key="main-2") {{ scheduledTime }}

        .d-flex.gap-5.mb-8(id="requiredDeliveryTime" v-if="!hasActiveCanteen")
            .w-100.w-sm-auto.min-w-sm-fix-150.border.border-2.border-radius-sm.min-h-fix-52.h-md-fix-52.d-flex.flex-column.py-2.px-4(:class="[asapInactive ? 'cursor-not-allowed' : 'cursor-pointer',{'border-26': asapChecked}]" @click="setAsap"   role="button")
                .d-flex.justify-content-between.gap-2.align-items-start
                    label.font-size-sm.mb-0(:class="{'text-73': asapInactive}") {{ $t('details.now') }}
                    .flex-center.position-relative.mt-1
                        icon(icon="fa-regular fa-circle" :class="{'text-e8': asapInactive}")
                        icon.position-absolute(icon="fa-solid fa-check-circle" v-if="asapChecked")                         
                transition(name="fade" mode="out-in")  
                    span.font-size-sm.text-73(v-if="!asapInactive && asapTime" key="asap-1") {{ asapTime }}
                    span.font-size-sm.text-73(v-else-if="asapInactive" key="asap-2") {{ $t('details.asap_unavailable') }}
            .w-100.w-sm-auto.min-w-sm-fix-150.border.border-2.border-radius-sm.min-h-fix-52.h-md-fix-52.d-flex.flex-column.py-2.px-4.cursor-pointer(:class="{'border-26': !asap}" @click="changeTime" role="button")
                .d-flex.justify-content-between.gap-2.align-items-start
                    label.font-size-sm.mb-0 {{ $t('details.schedule_later') }}
                    .flex-center.position-relative.mt-1
                        icon(icon="fa-regular fa-circle")
                        icon.position-absolute(icon="fa-solid fa-check-circle" v-if="!asap")    
                transition(name="fade" mode="out-in")  
                    span.font-size-sm.text-73(v-if="asap" key="scheduled-1") {{ $t('details.change_time') }}
                    span.font-size-sm.text-73(v-else-if="!asap && !$cart.loading && scheduledTime" key="scheduled-2") {{ scheduledTime }}

        template(v-if="!addressChange && address")          
            .d-flex.justify-content-between.align-items-center.gap-4.mb-4
                h3.mb-0 {{ $t('details.delivery_to') }}
                button.btn.btn-link(@click="changeAddress" v-if="!hasActiveCanteen") {{ $t('details.change_address') }}
            .overflow-hidden.w-100
                gmap-map.h-fix-150(:options="addressBlockMapOptoins" :key="`${address.latitude}-${address.longitude}`" :center="{lat: address.latitude, lng:address.longitude}")
                    gmap-marker(:position="{lat: address.latitude, lng:address.longitude}" :options="{cursor: 'default'}" icon="/images/marker-dropoff.svg")        
            .card-body.position-relative.d-flex.flex-column        
                button.position-absolute.z-1.btn.btn-white-primary.btn-pill.shadow-5.top--fix-20.right-fix-20(@click="edit")
                    icon.mr-2(icon="fa-solid fa-pencil-alt")
                    | {{ $t('base.edit') }}
                h4.d-flex.align-items-center
                    icon.mr-2(:icon="`fa-solid ${isCompanyAddress ? 'fa-briefcase' : 'fa-location-arrow'}`" :key="`addressIcon-${isCompanyAddress}-${_uid}`")
                    span.text-ellipsis-2.font-weight-semibold(v-if="address.name") {{address.name}}
                p.text-73.body-m.mb-2(v-if="address.full_address") {{address.full_address}}
                    template(v-if="address.sub_address") , {{address.sub_address}}
                p.d-flex.align-items-center.text-73.body-m.mb-2(v-if="$cart.phone")
                    icon.mr-2(icon="fa-solid fa-phone")
                    | {{$cart.phone}}
                p.d-flex.align-items-baseline.text-73.body-m.mb-2(v-if="$cart.comment")
                    icon.mr-2.relative.top-fix-3(icon="fa-solid fa-comment")
                    span.text-ellipsis {{$cart.comment}}
        template(v-else)
            h3 {{ $t('details.delivery_address') }}
            radio-address.border-bottom(
                v-for="(savedAddress, index) in $filteredAddresses"
                :key="`filteredAddress-${index}`"
                :value="selectedAddress ? selectedAddress.id : undefined"
                :address="savedAddress"
                @input="selectAddress(savedAddress)"
                hide-allowance 
                :editable="false")            
            radio-address.border-bottom(
                v-for="(savedAddress, index) in unavailableAddresses"
                :key="`unavailableAddress-${index}`"
                :address="savedAddress"
                disabled 
                hide-allowance 
                :editable="false")            
            button.btn.btn-add(@click="addNewAddress")   
                icon.mr-2.text-primary(icon="fa-solid fa-plus")
                | {{ $t('details.new_address') }}
            .d-flex.gap-4.mt-4(id="address_change_open")
                button.btn.btn-secondary.btn-block(@click="resetChangeAddress" :disabled="!orderAddress || (orderAddress && !orderAddress.id)") {{ $t('base.cancel') }}
                button.btn.btn-primary.btn-block(@click="setAddress" :disabled="!selectedAddress") {{ $t('details.select_saved') }}

    modal(ref="modalEditDeliveryInfo" :closable="false")    
        template(v-slot:header)
            .modal-header-text {{ $t('order.edit_order_delivery') }}
        template(v-slot:body)
            form-validation(
                    ref="form"
                    @valid="validatePhone"
                    @submit="submit")
                input-textarea(
                    v-model="subAddress"
                    :disabled="hasActiveCanteen"
                    :max="255"
                    :placeholder="$t('details.placeholder.sub_address')"
                    resize) {{$t('details.sub_address')}}                
                input-phone(
                    v-model="phone"
                    button-icon="fa-solid fa-save"
                    :button-text="isCompanyAddress ? $t('details.save_to_profile') : $t('details.save_to_address')"
                    type="phone"
                    :button-loading="loadingSave"
                    :button-disabled="phone === $cart.model.phone || !validPhone"
                    :key="`checkoutPhone-${$user.isLoggedIn}`"
                    @click="savePhoneNumber"
                    immediate required required-warn) {{$t('details.phone')}}
                input-textarea(
                    v-if="!hasActiveCanteen"
                    v-model="comment"
                    :max="255"
                    :placeholder="$t('details.placeholder.comment')"
                    resize) {{$t('details.comment')}}
        template(v-slot:footer)                
            button.btn.btn-secondary.btn-block(@click="cancelEdit" :disabled="loadingSave") {{ $t('base.cancel') }}
            button.btn.btn-primary.btn-block(@click="confirmEdit" :disabled="loadingSave || !validPhone") {{ $t('base.confirm') }}

</template>
